import { forwardRef, useMemo } from "react";
import { cn } from "@/utils/utils";
import { SavedAttachmentInfo } from "@/entities/attachments/types";
import FileUpload from "@/components/controls/FileUpload/FileUpload";
import AttachmentsFieldPopover from "@/components/controls/AttachmentsFieldPopover";

export interface AttachmentsFieldProps {
  value: SavedAttachmentInfo[] | null;
  onChange: (value: SavedAttachmentInfo[] | null) => void;
  name: string;
  /** Per-account configured copy for the upload button. Unset, the field spells
   *  the design system's own label. */
  placeholder?: string;
  className?: string;
  accept?: string;
  isSingleFile?: boolean;
  disabled?: boolean;
  /** Preview is the compact form and opens on the Small upload button; edit
   *  is the field composite — used by both the forms and the record fields —
   *  and opens on the Large drag-and-drop area; popover is the compact one-row
   *  form that opens the picker over itself. */
  variant?: "preview" | "edit" | "popover";
}

// The three modes of the file field. Preview is the compact form: it renders
// inline rather than behind a popover, putting the Small upload button — and
// afterwards the file stack — directly in the row, so the files stay visible
// while the record is read. Edit is the field composite used by the forms and
// by the record fields alike: the field owns a block of its own, the Large
// drag-and-drop area is the affordance, and a long list caps and reveals the
// rest on demand. Popover lists the stored files in the row but has no room
// for an inline upload affordance beside them, so it opens the picker over the
// field — it delegates to AttachmentsFieldPopover.
const AttachmentsField = forwardRef<HTMLDivElement, AttachmentsFieldProps>(
  (
    {
      value,
      onChange,
      name,
      placeholder,
      className,
      accept,
      isSingleFile,
      disabled,
      variant = "preview"
    },
    ref
  ) => {
    // A stable identity per stored list: FileUpload keys its prune effect on
    // this array, and a fresh [] each render would re-run it on every render.
    const attachments = useMemo(
      () => (Array.isArray(value) ? value : []),
      [value]
    );

    if (variant === "popover") {
      return (
        <AttachmentsFieldPopover
          ref={ref}
          value={value}
          onChange={onChange}
          name={name}
          placeholder={placeholder}
          className={className}
          accept={accept}
          isSingleFile={isSingleFile}
          disabled={disabled}
        />
      );
    }

    return (
      <div
        ref={ref}
        className={cn(
          "w-full min-w-0",
          variant === "preview" && "py-[2px]",
          className
        )}
        data-test={`${name}-input`}
      >
        <FileUpload
          onAttachmentsChange={onChange}
          initialAttachments={attachments}
          isPreviewLayout={variant === "preview"}
          label={placeholder}
          accept={accept}
          isSingleFile={isSingleFile}
          disabled={disabled}
          // A disabled field is read-only, not merely inert: the stored files
          // stay listed and openable, and the actions that would change them
          // are withheld rather than shown greyed out with nothing to explain
          // them — this field carries no disabled-reason tooltip.
          isReadOnly={disabled}
          dataTestPrefix={`${name}-file`}
        />
      </div>
    );
  }
);
AttachmentsField.displayName = "AttachmentsField";

export default AttachmentsField;
